import { useCallback, useEffect, useState } from 'react';

import { ExtendedDateRange, ScheduleTimeFrame } from 'common/contracts';
import { EIntervalsRepeat } from 'constants/enums';
import { timeUtils } from 'utils/scheduleUtils';

import RichText from 'components/RichText/RichText';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';

import Interval from './ScheduleOptions/Interval';
import Permanent from './ScheduleOptions/Permanent';
import Schedule from './ScheduleOptions/Schedule';

const TAB_TRIGGERS = [
  { value: 'permanent', label: 'Permanent' },
  { value: 'intervals', label: 'Intervals' },
  { value: 'schedule', label: 'Schedule' }
];

const ScheduleTab = ({
  values,
  formikProps,
  startHour,
  setStartHour,
  intervalCron,
  setIntervalCron
}: any) => {
  const { handleChange, handleBlur, touched, errors, setFieldValue } = formikProps;
  const [activeTab, setActiveTab] = useState('permanent');
  const [repeat, setRepeat] = useState(EIntervalsRepeat.NONE);
  const [weekDay, setWeekDay] = useState<number>(0);
  const [dayInMonth, setDayInMonth] = useState<number>(1);

  const [appearancesTableData, setAppearancesTableData] = useState<
    ScheduleTimeFrame[]
  >([]);
  const [editRangeDatesValues, setEditRangeDatesValues] =
    useState<ExtendedDateRange | null>(null);

  useEffect(() => {
    const timeFramesData: ScheduleTimeFrame[] =
      values.schedule?.timeFrames?.map(
        (date: ScheduleTimeFrame, index: number) => ({
          id: date.id || `id-${index}`,
          startTime: date.startTime,
          endTime: date.endTime,
          notes: date.notes
        })
      );

    setAppearancesTableData(timeFramesData);
  }, [values?.schedule?.timeFrames]);

  // Generate cron string when repeat, startHour, weekDay, or dayInMonth changes
  const generateCron = useCallback(() => {
    if (repeat === EIntervalsRepeat.NONE) {
      setIntervalCron('');
      return;
    }

    const newCron = timeUtils.generateCronString(
      repeat,
      startHour,
      weekDay as number,
      dayInMonth as number
    );

    if (newCron && newCron !== intervalCron) {
      setIntervalCron(newCron);
    }
  }, [repeat, startHour, weekDay, dayInMonth, intervalCron, setIntervalCron]);

  useEffect(() => {
    generateCron();
  }, [generateCron]);

  // Update form values when intervalCron changes
  useEffect(() => {
    if (values?.schedule?.permanent && intervalCron) {
      setFieldValue('schedule', {
        permanent: values?.schedule?.permanent,
        timeFrames: values?.schedule?.timeFrames,
        ...(repeat !== EIntervalsRepeat.NONE && { interval: intervalCron })
      });
    }
  }, [intervalCron, repeat, setFieldValue, values?.schedule?.permanent, values?.schedule?.timeFrames]);

  // Parse existing interval and set state values (only run when interval changes from external source)
  useEffect(() => {
    const interval = values?.schedule?.interval;

    if (!interval) {
      return;
    }

    const { dayInMonthFromCron, hourFromCron, weekDayFromCron } =
      timeUtils.getTimeFromCron(interval);

    const newRepeat = dayInMonthFromCron
      ? EIntervalsRepeat.MONTHLY
      : weekDayFromCron !== undefined
        ? EIntervalsRepeat.WEEKLY
        : hourFromCron
          ? EIntervalsRepeat.DAILY
          : EIntervalsRepeat.NONE;

    // Only update if values are actually different to prevent loops
    if (newRepeat !== repeat) setRepeat(newRepeat);
    if (dayInMonthFromCron && dayInMonthFromCron !== dayInMonth)
      setDayInMonth(dayInMonthFromCron);
    if (weekDayFromCron !== undefined && weekDayFromCron !== weekDay)
      setWeekDay(weekDayFromCron);
    if (hourFromCron && hourFromCron !== startHour) setStartHour(hourFromCron);
  }, [values?.schedule?.interval]);

  const resetScheduleValues = () => {
    setEditRangeDatesValues(null);
    setStartHour('');
    setIntervalCron('');
    setRepeat(EIntervalsRepeat.NONE);
  };

  // Set permanent flag based on active tab
  useEffect(() => {
    if (activeTab === 'schedule') {
      // When switching to Schedule tab, ensure permanent is false
      setFieldValue('schedule', {
        permanent: false,
        timeFrames: values?.schedule?.timeFrames || []
      });
    } else if (activeTab === 'permanent' || activeTab === 'intervals') {
      // When switching to Permanent or Intervals tabs, ensure permanent is true
      setFieldValue('schedule', {
        permanent: true,
        timeFrames: values?.schedule?.timeFrames || [],
        ...(intervalCron && { interval: intervalCron })
      });
    }
  }, [activeTab]);

  return (
    <div className="flex flex-col mb-8">
      <RichText
        title="Scheduled Offer"
        description="Set specific dates & hours to your offer"
        className="mb-9"
      />
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-zinc-100 p-1 rounded-xl grid grid-cols-3 h-10 w-full">
          {TAB_TRIGGERS.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-zinc-900 text-zinc-500 rounded-lg font-normal h-8"
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="permanent" className="mt-9">
          <Permanent
            values={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            touched={touched}
            errors={errors}
          />
        </TabsContent>

        <TabsContent value="intervals" className="mt-9">
          <Interval
            repeat={repeat}
            setRepeat={setRepeat}
            startHour={startHour}
            setStartHour={setStartHour}
            weekDay={weekDay}
            setWeekDay={setWeekDay}
            dayInMonth={dayInMonth}
            setDayInMonth={setDayInMonth}
            values={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            touched={touched}
            errors={errors}
            setFieldValue={setFieldValue}
          />
        </TabsContent>

        <TabsContent value="schedule" className="mt-9">
          <Schedule
            formikProps={formikProps}
            editRangeDatesValues={editRangeDatesValues}
            setEditRangeDatesValues={setEditRangeDatesValues}
            appearancesTableData={appearancesTableData}
            resetScheduleValues={resetScheduleValues}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ScheduleTab;
